import { ActionCenterViewHandler } from '@/hooks/useActionCenterViewController';
import { ActionCenterItemV2 } from '@cloudcost/shared-types/action-center-v2';

interface AllocationHandlerOptions {
  setSelectedItemId: (id: string) => void;
  setIsDialogOpen: (open: boolean) => void;
  setFocusFilters: (filters: Record<string, string>) => void;
  cloudAccountExists: (id: string) => boolean;
  resourceOwnerExists: (id: string) => boolean;
  setAssignmentFilterMode: (
    mode: 'allUnassigned' | 'missingOwner' | 'missingSystem' | 'all',
  ) => void;
}

function resolveCloudAccountId(item: ActionCenterItemV2): string | null {
  const cloudAccountId =
    item.cloud_account_id ??
    (item as any).id_cloud_account ??
    (item as any).cloud_account_id ??
    null;
  // Only return if it's a valid number (not 0, null, or undefined)
  if (cloudAccountId != null && cloudAccountId !== 0 && cloudAccountId !== '') {
    return String(cloudAccountId);
  }
  return null;
}

function resolveOwnerId(item: ActionCenterItemV2): string | null {
  // Prefer owner_id, but only if it's actually set (not 0 or empty)
  const ownerId = item.owner_id ?? (item as any).id_owner ?? null;
  if (ownerId != null && ownerId !== 0 && ownerId !== '') {
    return String(ownerId);
  }
  // Don't use responsible_id as fallback for resource owner filter
  // responsible_id is for a different purpose (who is responsible for the action)
  return null;
}

/**
 * Determines which Allocation filter (cloud account or owner) should be focused
 * when a user selects an item from Action Center. Sets both filters if both are available.
 */
export function createAllocationActionCenterHandler({
  setSelectedItemId,
  setIsDialogOpen,
  setFocusFilters,
  cloudAccountExists,
  resourceOwnerExists,
  setAssignmentFilterMode,
}: AllocationHandlerOptions): ActionCenterViewHandler {
  return ({ item, clearSelection }) => {
    setSelectedItemId(String(item.id));
    setIsDialogOpen(true);

    const filtersToSet: Record<string, string> = {};
    
    const cloudAccountId = resolveCloudAccountId(item);
    // Set filter only if ID exists and is not empty
    if (cloudAccountId && cloudAccountId.trim() !== '') {
      filtersToSet.cloudAccount = cloudAccountId;
    }

    const ownerId = resolveOwnerId(item);
    // Set filter only if ID exists and is not empty
    if (ownerId && ownerId.trim() !== '') {
      filtersToSet.resourceOwner = ownerId;
    }

    // Only set filters if we have at least one valid filter
    if (Object.keys(filtersToSet).length > 0) {
      setFocusFilters(filtersToSet);
    }

    const subtype = (item.subtype ?? '').toLowerCase();
    if (subtype === 'missingowner') {
      setAssignmentFilterMode('missingOwner');
    } else if (subtype === 'missingsystem') {
      setAssignmentFilterMode('missingSystem');
    } else {
      setAssignmentFilterMode('allUnassigned');
    }
    clearSelection();
  };
}



